import { $amplitudeExperimentsAtom } from '@client/core/atoms/amplitude.js';
import {
  AfsPlacementId,
  ConsentStatus,
  ExperimentVariants,
  FinnLegendaryAfsPlacementId,
  Vertical
} from '@schibsted-nmp/advertising-shared';

import { $config } from '../../core/atoms/config.js';

type ThirdPlacementVariantMap = Partial<
  Record<AfsPlacementId | FinnLegendaryAfsPlacementId, string>
>;

export default function getAfsChannel(
  placementId: AfsPlacementId | FinnLegendaryAfsPlacementId
): string {
  // Custom channels can be edited at adsense.google.com
  const channelsConfigMap = {
    finn: {
      vertical: {
        [Vertical.Recommerce]: '1541148774'
      },
      consent: {
        [ConsentStatus.Accepted]: '4684201339',
        [ConsentStatus.Rejected]: '9888426555'
      },
      placement: {
        [AfsPlacementId.AfsContainer1]: '3018295631',
        [AfsPlacementId.AfsContainer2]: '4572767102',
        [AfsPlacementId.AfsContainerBottom]: '2926989810'
      },
      thirdPlacementExperiment: {
        [ExperimentVariants.Control]: {
          [AfsPlacementId.AfsContainer1]: '9633522094',
          [AfsPlacementId.AfsContainerBottom]: '7007358753'
        },
        [ExperimentVariants.Treatment]: {
          [AfsPlacementId.AfsContainer1]: '6536585464',
          [AfsPlacementId.AfsContainerBottom]: '5223503790'
        }
      }
    },
    dba: {
      vertical: {
        [Vertical.Recommerce]: '7595169273'
      },
      consent: {
        [ConsentStatus.Accepted]: '5124122443',
        [ConsentStatus.Rejected]: '5399454618'
      },
      placement: {
        [AfsPlacementId.AfsContainer1]: '1460209601',
        [AfsPlacementId.AfsContainer2]: '9636191445',
        [AfsPlacementId.AfsContainerBottom]: '4386552005'
      },
      thirdPlacementExperiment: {}
    },
    tori: {
      vertical: {
        [Vertical.Recommerce]: '6734179364'
      },
      consent: {
        [ConsentStatus.Accepted]: '9803002315',
        [ConsentStatus.Rejected]: '1972613759'
      },
      placement: {
        [AfsPlacementId.AfsContainer1]: '4031994407',
        [AfsPlacementId.AfsContainer2]: '4079036994',
        [AfsPlacementId.AfsContainerBottom]: '5839706570'
      },
      thirdPlacementExperiment: {
        [ExperimentVariants.Control]: {
          [AfsPlacementId.AfsContainer1]: '7040242251',
          [AfsPlacementId.AfsContainerBottom]: '1865472049'
        },
        [ExperimentVariants.Treatment]: {
          [AfsPlacementId.AfsContainer1]: '2570647659',
          [AfsPlacementId.AfsContainerBottom]: '3260912795'
        }
      }
    },
    blocket: {
      vertical: {
        [Vertical.Recommerce]: '1809641818'
      },
      consent: {
        [ConsentStatus.Accepted]: '9864936243',
        [ConsentStatus.Rejected]: '3606649654'
      },
      placement: {
        [AfsPlacementId.AfsContainer1]: '9230511427',
        [AfsPlacementId.AfsContainerBottom]: '7852305986'
      },
      thirdPlacementExperiment: {}
    }
  };

  try {
    const config = $config.get();
    const { brand, consent, vertical } = config ?? {};

    const { experiments } = $amplitudeExperimentsAtom.get();

    const thirdPlacementVariant =
      experiments['advt-afs-3rd-placement-tori'] ??
      experiments['advt-afs-3rd-placement-finn'];

    const brandConfig =
      channelsConfigMap[brand as keyof typeof channelsConfigMap];

    const consentMap = brandConfig.consent;
    const consentChannel = consent ? consentMap[consent] : '';

    const verticalMap = brandConfig.vertical;
    const verticalChannel = verticalMap[vertical as keyof typeof verticalMap];

    const placementMap = brandConfig.placement;
    const placementChannel =
      placementMap[placementId as keyof typeof placementMap];

    const thirdPlacementMap = brandConfig.thirdPlacementExperiment;
    const thirdPlacementVariantMap: ThirdPlacementVariantMap | undefined =
      thirdPlacementVariant
        ? thirdPlacementMap[
            thirdPlacementVariant as keyof typeof thirdPlacementMap
          ]
        : {};
    const thirdPlacementChannel = thirdPlacementVariantMap[placementId] ?? '';

    const channelsString = [
      consentChannel,
      verticalChannel,
      placementChannel,
      thirdPlacementChannel
    ]
      .filter(Boolean)
      .join('+');

    return channelsString;
  } catch (error) {
    const message = 'Failed to get AFS channels:';
    console.error(message, error);
    return '';
  }
}
